import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import ActivityLog from "@/services/activity-log";
import type { PaginatedActivityLogResponse } from "@/dtos/ActivityLog.dto";

export interface ActivityLogsParams {
  contactId: string;
  organizationId: string;
  page?: number;
  limit?: number;
  taskType?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useActivityLogs(
  params: Ref<ActivityLogsParams | null> | ActivityLogsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["activityLogs", "disabled"];
    return ["activityLogs", p.contactId, p.organizationId, p.page, p.limit, p.taskType ?? "all"];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.contactId && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const result = await ActivityLog.getActivityLogs({
        contactId: p.contactId,
        organizationId: p.organizationId,
        page: p.page || 1,
        limit: p.limit || 10,
        ...(p.taskType ? { taskType: p.taskType } : {}),
      });

      if (result.data.status) {
        return result.data.data as PaginatedActivityLogResponse;
      }

      throw new Error(result.data.msg || "Failed to load activity logs");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
